import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePriceGroup } from 'core/contexts/priceGroup';
import { UnitType } from '@utriper/core';
import { Field, Form, FormSpy } from 'react-final-form';
import { GroupedSelect } from '@utriper/core-ui';
import { Box, FormHelperText, Grid2 } from '@mui/material';
import Title from 'modules/Pricing/components/Title';
import PriceGroupGrid from 'modules/Pricing/components/PriceGroupGrid';
import { Label } from '../../styles';
import ActionButtons from '../ActionButtons';

const AddUnitForm = ({ priceGroupId, handleClose = () => {} }) => {
    const { i18n, t } = useTranslation();
    const translate = (key) => t(`price_group_id.add_unit_form.${key}`);
    const { addUnit, unitsPriceGroup, units, bookingOrigins, prices: rangePrices, submittingPriceGroup, validatePrices } = usePriceGroup();

    const [submitted, setSubmitted] = useState(false);
    const [unitSelected, setUnitSelected] = useState(null);
    const [prices, setPrices] = useState([]);

    const unitsOptions = useMemo(() => {
        const options = units?.map((unit) => {
            if (unitsPriceGroup.some((unitPriceGroup) => unitPriceGroup?.unitType === unit?.typeId)) return false;

            return {
                id: unit.id,
                name: unit?.name,
                group: UnitType.getTranslatedName(UnitType.types, unit?.typeId, i18n.language),
            };
        });

        return options.filter(Boolean);
    }, [unitsPriceGroup, units, i18n]);

    // Load ranges
    useEffect(() => {
        if (!unitSelected?.id) return;

        if (Array.isArray(rangePrices) && rangePrices?.length > 0) {
            const rangesMap = new Map();

            for (const { quantityFrom, quantityTo } of rangePrices) {
                const key = `${quantityFrom}-${quantityTo}`;

                if (!rangesMap.has(key)) {
                    let prices = [
                        {
                            unitId: unitSelected.id,
                            price: 0,
                            bookingOriginId: null,
                        },
                    ];

                    for (const bookingOriginId of bookingOrigins ?? []) {
                        prices.push({
                            unitId: unitSelected.id,
                            price: 0,
                            bookingOriginId,
                        });
                    }

                    rangesMap.set(key, { quantityFrom, quantityTo, prices });
                }
            }

            const ranges = [...rangesMap.values()]?.map((range, idx) => ({ rowId: idx, ...range }));
            setPrices([...ranges]);
        }
    }, [unitSelected, rangePrices, bookingOrigins]);

    const handleSubmit = async ({ unitId }) => {
        await addUnit(priceGroupId, unitId, prices);
        handleClose();
    };

    const validateForm = (values) => {
        const errors = {};

        if (!Number.isFinite(values?.unitId)) {
            errors.unitId = translate('validate_form.required');
        }

        const priceErrors = validatePrices(prices, units);

        errors.prices = priceErrors;

        return errors;
    };

    const handleChangeForm = ({ values }) => {
        const { unitId } = values;
        if (!Number.isFinite(unitId)) return;

        const unitData = units.find((unit) => unit.id === unitId);
        setUnitSelected({ ...unitData });
    };

    return (
        <Form
            key={`price-group-form-units-${priceGroupId}`}
            onSubmit={handleSubmit}
            validate={validateForm}
            render={({ errors, handleSubmit, form }) => {
                const hasPriceErrors = submitted && errors?.prices;
                const priceErrors = errors?.prices ?? [];

                return (
                    <form key={`price-group-form-units-${priceGroupId}`} onSubmit={handleSubmit} noValidate>
                        <Box component="section" py={2}>
                            <Title title={translate('title')} description={translate('description')} />

                            {/* Form */}
                            <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                                <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="4px">
                                    <Label>{translate('form.unit')}</Label>
                                    <Field name="unitId" component={GroupedSelect} label={translate('form.unit_label')} options={unitsOptions} />
                                </Grid2>

                                {prices?.length > 0 && unitSelected?.id && (
                                    <Box width="100%" maxHeight="640px" overflow="auto" mt={1}>
                                        <PriceGroupGrid
                                            data={{
                                                units: [unitSelected],
                                                bookingOrigins,
                                                prices,
                                            }}
                                            actions={{
                                                setPrices,
                                            }}
                                        />
                                    </Box>
                                )}

                                {/* Errors Ranges */}
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxHeight: '78px',
                                        overflowY: 'auto',
                                        mt: 2,
                                    }}
                                >
                                    {hasPriceErrors && priceErrors?.length > 0 && priceErrors.map((error) => <FormHelperText error={hasPriceErrors}>{error}</FormHelperText>)}
                                </Box>
                            </Grid2>

                            <ActionButtons
                                handleSubmit={(e) => {
                                    setSubmitted(true);
                                    return handleSubmit(e);
                                }}
                                handleClose={(e) => {
                                    handleClose();
                                    return form.reset(e);
                                }}
                                submitting={submittingPriceGroup}
                                labelNext={translate('add_button')}
                            />
                        </Box>

                        {/* Form Spy */}
                        <FormSpy subscription={{ values: true }} onChange={handleChangeForm} />
                    </form>
                );
            }}
        />
    );
};

export default AddUnitForm;
